import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../config';
import {
  addInteraction,
  deleteInteraction,
  editInteraction,
  getInteraction,
  getInteractions,
} from './methods';
import { EditInteractionRequest } from './types';

const useGetInteractionsQuery = () => {
  const { isPending, isSuccess, isError, data } = useQuery({
    queryFn: getInteractions,
    queryKey: [queryKeys.interactionsKey],
    staleTime: Infinity,
  });

  return { data, isError, isPending, isSuccess };
};

const useGetInteractionQuery = (id: number) => {
  const { isPending, isSuccess, isError, data } = useQuery({
    enabled: !!id,
    queryFn: () => getInteraction(id),
    queryKey: [queryKeys.interactionsKey, id],
  });

  return { data, isError, isPending, isSuccess };
};

const useAddInteractionMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: addInteractionMutation,
  } = useMutation({
    mutationFn: addInteraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.interactionsKey] });
    },
  });

  return { addInteractionMutation, isError, isPending, isSuccess };
};

const useEditInteractionMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editInteractionMutation,
  } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditInteractionRequest }) =>
      editInteraction(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.interactionsKey] });
    },
  });

  return { editInteractionMutation, isError, isPending, isSuccess };
};

const useDeleteInteractionMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteInteractionMutation,
  } = useMutation({
    mutationFn: deleteInteraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.interactionsKey] });
    },
  });

  return { deleteInteractionMutation, isError, isPending, isSuccess };
};

export {
  useAddInteractionMutation,
  useDeleteInteractionMutation,
  useEditInteractionMutation,
  useGetInteractionQuery,
  useGetInteractionsQuery,
};
